import { usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';
import { PageProps } from '../types';

export default function PaymentReferenceForm({
  paymentReference,
}: {
  paymentReference: string | null,
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const { charity } = usePage<PageProps>().props;

  const { register, handleSubmit, watch, formState: { errors, defaultValues, isSubmitting }, setError } = useForm({
    defaultValues: {
      payment_reference: paymentReference,
    },
  });

  const reference = watch('payment_reference');
  const length = reference?.length || 0;
  const maxLength = 20;

  const [
    editPaymentReference,
    { errors: serverErrors },
  ] = useMutation(routes.edit_payment_reference(), {
    onSuccess: () => {
      notifier.notify(t('backend:payment_reference_saved'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(editPaymentReference)} className="space-y-6">
      <Card panels>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

          <InputGroup>
            <InputLabel valid={!errors.payment_reference} htmlFor="payment_reference">
              {t('backend:payment_reference')}
            </InputLabel>
            <InputDescription>
              {t('backend:payment_reference_description')}
              {' '}
              <span className={classNames('bg-blue-200 text-blue-700 px-1 inline-block text-sm rounded-sm', length === 0 && 'opacity-50')}>
                <strong>{length}</strong> / {maxLength}
              </span>
            </InputDescription>
            <Input
              {...register('payment_reference')}
              id="payment_reference"
              maxLength={maxLength}
              aria-invalid={!!errors.payment_reference}
            />
            <div className="mt-2">
              <Trans i18nKey="backend:example_payment_reference" values={{ reference: `${reference || ''} 12345 Running Event ${charity.title}` }}>
                <span className="font-medium text-slate-500"/>
              </Trans>
            </div>
            <ErrorMessage attribute={t('backend:payment_reference')} error={errors.payment_reference} />
          </InputGroup>
        </div>
        <div className="!py-4 border-slate-100 bg-slate-50 text-right">
          <Button
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
